import { doc, getDoc } from "firebase/firestore";
import { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router";
import { db } from "../index";
import AppLoader from "../UI/AppLoader";
import classes from "./ProductInfo.module.css";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { CartContext } from "./CartContext";
import ClipLoader from "react-spinners/PulseLoader";
import Modal from "react-bootstrap/Modal";
import SimilarProducts from "./SimilarProducts";
import { blurContext } from "./BlurContext";
import Container from "react-bootstrap/Container";
import { SwiperSlide, Swiper } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import { Pagination, Navigation, EffectFade } from "swiper/modules";

function ProductInfo() {
	//for naviagtion (obviously :) )
	const navigate = useNavigate();

	// get dynamically generated url
	const getId = useParams();
	const productId = getId.id;

	//context for products in cart
	const { addProduct, removeProduct, cartItems, changeQuantity } =
		useContext(CartContext);

	//product and sizes data
	const [product, setProduct] = useState();
	const [selectedSizeId, setSelectedSizeId] = useState("");

	//check if product is already in cart
	const productInCart = cartItems.find(
		(product) => product.id === productId && product.size === selectedSizeId
	);

	//import blur context
	const { changeBlurState } = useContext(blurContext);

	//loading and error state
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [imageLoading, setImageLoading] = useState(true);

	// manage quantity state
	const [counter, setCounter] = useState(1);

	//state to display modal prompt for user to select size
	const [showModal, setShowModal] = useState(false);

	//state to monitor if size has been changed
	const [productSizeInCart, setProductSizeInCart] = useState(false);

	// effect to listen for press of backward or forward buttons in the browser to change blur state to false
	useEffect(() => {
		return () =>
			window.addEventListener("popstate", () => changeBlurState(false));
	}, [changeBlurState]);

	//load product data from database
	useEffect(() => {
		const loadProduct = async () => {
			try {
				const product_reference = doc(db, "items", productId);
				const productData = await getDoc(product_reference);
				setProduct({ ...productData.data(), id: productData.id });
			} catch (error) {
				console.error(error);
				setError(true);
			} finally {
				setLoading(false);
			}
		};
		loadProduct();
	}, [productId]);

	//update state if product is in cart
	useEffect(() => {
		if (!productInCart) {
			setCounter(1);
			return;
		}
		setSelectedSizeId(productInCart?.size);
		setCounter(productInCart?.quantity);
	}, [productInCart]);

	//update product in cart if state changes
	useEffect(() => {
		if (!productInCart || productInCart?.quantity === counter) {
			return;
		}
		changeQuantity(productId, counter, selectedSizeId);
	}, [changeQuantity, productId, counter, selectedSizeId, productInCart]);

	//show loading animation if image hasn't loaded
	useEffect(() => {
		const image = new Image();
		image.src = product?.itemImg;
		image.onload = () => {
			setImageLoading(false);
		};
	}, [product]);

	const goBack = () => {
		navigate("/products");
	};

	const increment = () => {
		setCounter((prevCnt) => prevCnt + 1);
	};

	const decrement = () => {
		if (counter > 0) {
			setCounter((prevCnt) => prevCnt - 1);
		}
	};

	//function to trigger modal
	const launchModal = () => {
		setShowModal(true);
		changeBlurState(true);
	};

	// this function is to check if a size has been selected, if so proceed to add item to cart,
	//if not show modal prompting user to select a size
	const addToCart = () => {
		if (!selectedSizeId) {
			launchModal();
			return;
		}
		addProduct(productId, counter, selectedSizeId);
	};

	// check if a product and a particular size has been added to cart
	//if both conditions are met disable select size
	useEffect(() => {
		if (productInCart) {
			setProductSizeInCart(true);
		} else {
			setProductSizeInCart(false);
		}
	}, [cartItems, productId, selectedSizeId, productInCart]);

	return (
		<>
			{loading && (
				<AppLoader color={"#A8C4B8"} loading={loading} size={20} />
			)}
			{!loading && !error && (
				<>
					<span className={classes.backArrow} onClick={goBack}>
						<img src="/images/back.png" alt="back-btn" />
					</span>
					<div className={classes.card}>
						<Container>
							<Row className="pt-5">
								<Col sm={5} md={5} lg={5} xl={5} xxl={5}>
									{imageLoading && (
										<div className={classes.imageLoader}>
											<ClipLoader
												color={"#A8C4B8"}
												loading={imageLoading}
												size={10}
												aria-label="Loading Spinner"
												data-testid="loader"
											/>
										</div>
									)}
									{!imageLoading && (
										<div className={classes.imageContainer}>
											<Swiper
												style={{
													height: "100%",
												}}
												modules={[
													Pagination,
													Navigation,
													EffectFade,
												]}
												navigation={true}
												effect={"fade"}
												loop={true}
												pagination={{
													clickable: true,
												}}
												spaceBetween={0}
												slidesPerView={1}
											>
												{product.itemImg.map(
													(eachImg, index) => (
														<SwiperSlide
															key={index}
														>
															<div
																className={
																	classes.image
																}
															>
																<img
																	src={
																		eachImg
																	}
																	alt={
																		product.itemName
																	}
																/>
															</div>
														</SwiperSlide>
													)
												)}
											</Swiper>
										</div>
									)}
								</Col>
								<Col sm={7} md={7} lg={7} xl={7} xxl={7}>
									<Col>
										<div className={classes.productDetail}>
											<div className={classes.name}>
												<p
													style={{
														borderBottom:
															"2px solid #ccc",
													}}
												>
													{product.itemName}
												</p>
												<div
													style={{
														marginBottom: "0.5rem",
													}}
												>
													<span
														className={
															classes.available
														}
													>
														<img
															src="/images/check.png"
															alt="available"
															width={22}
														/>{" "}
														Product Available
													</span>
												</div>

												<p>${product.itemPrice}</p>
											</div>
											<div>
												<Form.Select
													className={classes.select}
													value={selectedSizeId}
													required
													onChange={(e) => {
														setSelectedSizeId(
															e.target.value
														);
													}}
												>
													<option
														disabled={
															productSizeInCart
														}
														value={""}
													>
														Select Size
													</option>

													{product?.sizes.map(
														(size) => (
															<option
																key={size.sku}
																value={size.sku}
															>
																{size.name}
															</option>
														)
													)}
												</Form.Select>
											</div>
											<div className={classes.quantity}>
												<p>Quantity</p>

												<div
													className={
														classes.quantityBtnContainer
													}
												>
													<Button
														onClick={decrement}
														disabled={counter === 1}
													>
														-
													</Button>
													<p>{counter}</p>
													<Button onClick={increment}>
														+
													</Button>
												</div>
											</div>
											<div className={classes.cartButton}>
												{productInCart ? (
													<>
														<Button
															onClick={(e) =>
																removeProduct(
																	productId,
																	selectedSizeId
																)
															}
														>
															Remove Product
														</Button>
													</>
												) : (
													<>
														<Button
															onClick={addToCart}
														>
															Add to cart
														</Button>
													</>
												)}
											</div>
										</div>
									</Col>
								</Col>
							</Row>
							<Row className="mt-5">
								<Col>
									<div className={classes.productDescription}>
										<h6
											style={{
												borderBottom: "1px solid #ccc",
												padding: "0.5rem",
											}}
										>
											Product Details
										</h6>
										<p
											style={{
												padding: "0.3rem",
												fontFamily:
													"Roboto, sans-serif",
											}}
										>
											{product.itemDescription}
										</p>
									</div>
								</Col>
							</Row>
						</Container>
					</div>

					{product && <SimilarProducts product={product} />}
				</>
			)}
			{!loading && error && (
				<Container>
					<div
						style={{
							display: "flex",
							alignContent: "center",
							justifyContent: "center",
							width: "100%",
							marginTop: "5%",
							marginLeft: "1rem",
							marginRight: "1rem",
						}}
					>
						<Alert>
							Sorry there was an error loading the product, please
							try again!!
						</Alert>
					</div>
				</Container>
			)}
			{/* Code below is to display modal prompt for user to select size */}
			<Modal
				show={showModal}
				onHide={() => {
					setShowModal(false);
					changeBlurState(false);
				}}
				size="sm"
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>Attention!!</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Alert variant="light">
						Please select a size before adding to cart.
					</Alert>
				</Modal.Body>
			</Modal>
		</>
	);
}

export default ProductInfo;
